"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";
import Image from "next/image";
import { MessageCircle, ShieldCheck, Clock } from "lucide-react";
import { SITE } from "../lib/site";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";

type Slide = {
  image: string;
  alt: string;
  eyebrow: string;
  title: string;
  highlight: string;
  subtitle: string;
};

const SLIDES: Slide[] = [
  {
    image: "/assets/images/Calentador.jpg",
    alt: "Técnico certificado realizando mantenimiento de calentador a gas",
    eyebrow: "Calentadores a gas y eléctricos",
    title: "Sin agua caliente esta",
    highlight: "noche.",
    subtitle:
      "Diagnóstico en sitio, repuesto original y garantía por escrito el mismo día.",
  },
  {
    image: "/assets/images/Estufa1.jpg",
    alt: "Estufa a gas con llama azul siendo revisada",
    eyebrow: "Estufas y cocinas a gas",
    title: "Llama amarilla, fugas, perillas",
    highlight: "duras.",
    subtitle:
      "Técnicos certificados por el SENA. Revisamos, calibramos y dejamos tu estufa segura.",
  },
  {
    image: "/assets/images/Horno.jpg",
    alt: "Técnico reparando horno empotrado",
    eyebrow: "Hornos eléctricos y a gas",
    title: "Horno que no calienta o no",
    highlight: "enciende.",
    subtitle:
      "Reparamos termostatos, resistencias y tarjetas de control de todas las marcas.",
  },
  {
    image: "/assets/images/Estufa2.jpg",
    alt: "Estufa empotrada en cocina moderna",
    eyebrow: "Plomería y reparaciones locativas",
    title: "Fugas, filtraciones y daños",
    highlight: "locativos.",
    subtitle:
      "Cuadrilla con experiencia en gas, plomería, eléctrica y enchape. Una sola visita.",
  },
];

export default function HeroCarousel() {
  return (
    <div className="relative h-[100svh] w-full overflow-hidden bg-brand-950">
      <Swiper
        modules={[Autoplay, EffectFade, Pagination]}
        autoplay={{ delay: 6000, disableOnInteraction: false }}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        loop
        pagination={{ clickable: true, el: ".vega-hero-pagination" }}
        className="h-full w-full"
      >
        {SLIDES.map((slide, index) => (
          <SwiperSlide key={slide.image} className="!flex">
            <div className="relative h-full w-full">
              <Image
                src={slide.image}
                alt={slide.alt}
                fill
                priority={index === 0}
                sizes="100vw"
                className="object-cover"
              />
              {/* Overlay degradado para legibilidad */}
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-r from-brand-950/95 via-brand-950/70 to-brand-950/30"
              />
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-brand-950 via-brand-950/20 to-transparent"
              />

              {/* Contenido */}
              <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-4 pb-24 sm:px-6 sm:pb-28 lg:px-8 lg:pb-32">
                <div className="max-w-3xl">
                  <span
                    className="animate-in fade-in slide-in-from-bottom-4 fill-mode-both duration-500 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-flame-300 backdrop-blur-sm"
                    style={{ animationDelay: "100ms" }}
                  >
                    <span className="size-1.5 rounded-full bg-flame-400 animate-pulse" />
                    {slide.eyebrow}
                  </span>
                  <h2
                    className="animate-in fade-in slide-in-from-bottom-6 fill-mode-both duration-700 mt-5 font-display text-4xl font-extrabold leading-[1.05] text-white text-balance sm:text-5xl md:text-6xl lg:text-7xl"
                    style={{ animationDelay: "250ms" }}
                  >
                    {slide.title}{" "}
                    <span className="text-flame-400">{slide.highlight}</span>
                  </h2>
                  <p
                    className="animate-in fade-in slide-in-from-bottom-4 fill-mode-both duration-700 mt-5 max-w-2xl text-lg text-white/85 text-pretty md:text-xl"
                    style={{ animationDelay: "400ms" }}
                  >
                    {slide.subtitle}
                  </p>

                  <div
                    className="animate-in fade-in slide-in-from-bottom-4 fill-mode-both duration-700 mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
                    style={{ animationDelay: "550ms" }}
                  >
                    <a
                      href={SITE.whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center justify-center gap-3 rounded-full bg-flame-600 px-7 py-4 text-base font-bold text-white shadow-xl transition hover:bg-flame-700 glow-flame sm:text-lg"
                    >
                      <MessageCircle className="size-5" />
                      Agenda por WhatsApp
                      <span className="ml-1 inline-block translate-x-0 transition group-hover:translate-x-1">
                        →
                      </span>
                    </a>
                    <a
                      href={`tel:${SITE.phone}`}
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/5 px-6 py-4 text-base font-bold text-white backdrop-blur-sm transition hover:bg-white/15"
                    >
                      Llamar {SITE.phonePretty}
                    </a>
                  </div>

                  <ul
                    className="animate-in fade-in fill-mode-both duration-700 mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-semibold text-white/85"
                    style={{ animationDelay: "700ms" }}
                  >
                    <li className="inline-flex items-center gap-2">
                      <Clock className="size-4 text-flame-400" />
                      Atención 24/7
                    </li>
                    <li className="inline-flex items-center gap-2">
                      <ShieldCheck className="size-4 text-flame-400" />
                      Técnicos certificados SENA
                    </li>
                    <li className="inline-flex items-center gap-2">
                      <span className="text-flame-400">★★★★★</span>
                      +15 años en Bogotá
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Paginación custom */}
      <div className="vega-hero-pagination pointer-events-auto absolute bottom-8 left-1/2 z-20 -translate-x-1/2 flex items-center gap-2" />

      {/* Etiqueta abajo derecha */}
      <div className="pointer-events-none absolute bottom-8 right-6 z-20 hidden items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-white/70 backdrop-blur sm:inline-flex">
        Bogotá · Cundinamarca
      </div>
    </div>
  );
}
